import { useEffect, useState } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export type InstallPwaButtonProps = {
  enabled: boolean;
  label: string | null;
};

/**
 * Floating "Install app" button in the bottom-left corner.
 *
 * Always shown when PWA is enabled in the backoffice, unless the app is
 * already running in standalone mode. On Chromium/Android/desktop, clicking
 * triggers the deferred `beforeinstallprompt` event. On iOS/Safari (or any
 * browser without a deferred prompt), it opens a modal with instructions
 * to add the site to the home screen.
 */
export function InstallPwaButton({ enabled, label }: InstallPwaButtonProps) {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    const mql = window.matchMedia("(display-mode: standalone)");
    const updateStandalone = () => {
      const standalone =
        mql.matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
      setIsStandalone(standalone);
    };
    updateStandalone();
    mql.addEventListener?.("change", updateStandalone);

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const installed = () => {
      setIsStandalone(true);
      setShowHelp(false);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installed);
    return () => {
      mql.removeEventListener?.("change", updateStandalone);
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, [enabled]);

  if (!enabled || isStandalone) return null;

  const text = label ?? "Instalar como app";

  const handleClick = async () => {
    if (deferred) {
      try {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") {
          setDeferred(null);
        }
        return;
      } catch {
        // fall through to manual instructions
      }
    }
    setShowHelp(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="fixed bottom-4 left-4 z-50 rounded-full bg-foreground/90 px-4 py-2 text-xs font-medium text-background shadow-lg backdrop-blur transition hover:bg-foreground"
        aria-label={text}
      >
        ⬇︎ {text}
      </button>
      {showHelp && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-start bg-black/40 p-4 sm:items-center sm:justify-center"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="max-w-sm rounded-lg border border-border bg-background p-4 text-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-3 font-medium">{text}</p>
            <p className="text-muted-foreground">
              Pulsa <b>Compartir</b> → <b>Añadir a pantalla de inicio</b>.
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowHelp(false)}
                className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
